import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import InputFile from '@/design_system/InputFile';
import InputText from '@/design_system/InputText';
import { Label } from '@/design_system/Label/Label';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCreateOrganization } from '@/models/organization';
import { useLocalStorageState } from '@/services/localStorage';
import { ORGANIZATION_LOCAL_STORAGE_KEY } from '@/utils/organization';

export const NewOrganizationForm = () => {
  const { t } = useLingui();

  const [, setSlug] = useLocalStorageState<string | null>(ORGANIZATION_LOCAL_STORAGE_KEY, null);

  const [name, setName] = useState('');
  const slug = name
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-z0-9-]/g, '');

  const [logo, setLogo] = useState<File | null>(null);

  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: createOrganization, isPending } = useCreateOrganization();

  const handleCreateOrganization = () => {
    if (!name) {
      setError(
        t({
          id: 'support.general.new-organization.create.confirmation.error.name',
          message: 'Name is required',
        })
      );

      return;
    }

    if (!logo) {
      setError(
        t({
          id: 'support.general.new-organization.create.confirmation.error.logo',
          message: 'Logo is required',
        })
      );

      return;
    }

    setShowCreateConfirmation(true);
  };

  const handleConfirmCreateOrganization = async () => {
    setError(null);

    try {
      await createOrganization({
        name,
        slug,
        logo: `data:${logo!.type};base64,${btoa(String.fromCharCode(...new Uint8Array(await logo!.arrayBuffer())))}`,
      });

      setSlug(slug);

      // Refresh the page to make sure the user's session has the new organization and role
      window.location.href = '/';
    } catch (error) {
      setError(
        (error as Error).message ??
          t({
            id: 'support.general.new-organization.create.confirmation.error',
            message: 'An error occurred while creating the organization',
          })
      );
    }
  };

  return (
    <Stack gap="1rem">
      <AlertBar
        type="info"
        title={
          <Trans id="support.general.new-organization.info">
            You are about to create a new organization. Here is what you need to know:
          </Trans>
        }
      >
        <ul>
          <li>
            <Trans id="support.general.new-organization.info.1">
              The organization will be created with some default settings, the rest must be
              configured manually.
            </Trans>
          </li>
          <li>
            <Trans id="support.general.new-organization.info.2">
              You will become the first administrator of the organization.
            </Trans>
          </li>
          <li>
            <Trans id="support.general.new-organization.info.3">
              You will be redirected to the organization after creation.
            </Trans>
          </li>
        </ul>
      </AlertBar>
      <Stack gap="2rem" padding="1rem 0 0 0">
        <InputText
          size="medium"
          label={<Trans id="support.general.new-organization.name">Name</Trans>}
          value={name}
          onChange={setName}
          messageType="info"
          messageText={
            slug
              ? t({
                  id: 'support.general.new-organization.name.slug',
                  message: `The slug will be: ${slug}`,
                })
              : undefined
          }
        />
        <Stack gap="0.5rem">
          <Label label={t({ id: 'support.general.new-organization.logo', message: 'Logo' })} />
          {logo ? (
            <PhotoCard
              size="xlarge"
              name="logo"
              alt=""
              url={URL.createObjectURL(logo)}
              onDelete={() => setLogo(null)}
            />
          ) : (
            <InputFile
              ariaLabel={t({
                id: 'settings.general.new-organization.logo.upload',
                message: 'Upload organization logo',
              })}
              type="photo"
              allowsMultiple={false}
              onUpload={(files) => setLogo(files[0] ?? null)}
            />
          )}
        </Stack>
        <Button
          variant="primary"
          size="medium"
          onPress={handleCreateOrganization}
          isLoading={isPending}
        >
          <Trans id="support.general.new-organization.create">Create organization</Trans>
        </Button>
        {error && <Paragraph color="danger">{error}</Paragraph>}
      </Stack>
      <ConfirmationDialog
        title={
          <Trans id="support.general.new-organization.create.confirmation.title">
            Create organization
          </Trans>
        }
        confirm={
          <Trans id="support.general.new-organization.create.confirmation.confirm">Confirm</Trans>
        }
        confirmButtonVariant="primary"
        showDialog={showCreateConfirmation}
        setShowDialog={setShowCreateConfirmation}
        onConfirm={handleConfirmCreateOrganization}
      >
        <Paragraph>
          <Trans id="support.general.new-organization.create.confirmation.description">
            Are you sure you want to create the <strong>{name}</strong> organization?
          </Trans>
        </Paragraph>
      </ConfirmationDialog>
    </Stack>
  );
};
